// The escalation ladder, as a timeline. Lifted from the Andon app's `Ladder`.
//
// This is the view that makes the promise legible. An operator who can see
// who has been told, who is next, and when, does not have to walk off looking
// for someone — which is the behaviour the whole system is trying to replace.
//
// The notes on each rung are the server's words, not this client's: it is the
// server that knows the policy and the clock, and a note translated here would
// be a sentence this app could get wrong about an escalation it did not make.

import type { LadderStepVM } from '@/api/andon/types';
import { SectionLabel } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';

const DOT: Record<LadderStepVM['state'], string> = {
  done: 'var(--pos)',
  hot: 'var(--danger)',
  waiting: 'var(--line)',
};

export function Ladder({ steps }: { steps: LadderStepVM[] }) {
  const t = useT();
  return (
    <section style={{ marginTop: 14 }}>
      <SectionLabel style={{ marginBottom: 11 }}>{t.escalationT}</SectionLabel>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {steps.map((step, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={`${step.who}-${i}`} style={{ display: 'flex', gap: 12 }}>
              <span aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    background: DOT[step.state],
                    marginTop: 4,
                    animation: step.state === 'hot' ? 'ipPulse 2.4s infinite' : 'none',
                  }}
                />
                {!last && <span style={{ width: 1.5, flex: 1, background: 'var(--line)', marginTop: 3 }} />}
              </span>
              <span style={{ flex: 1, paddingBottom: last ? 0 : 15 }}>
                <span style={{ display: 'block', font: '700 12.5px Manrope,sans-serif', color: step.state === 'waiting' ? 'var(--sub)' : 'var(--ink)' }}>
                  {step.who}
                </span>
                <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>{step.note}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
